import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { lowerCase } from 'lodash';
import {
  Button,
  DatePicker,
  Select,
  Row,
  Col,
  Form,
  notification,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import * as API from 'aws-amplify/api';
import { ConsoleLogger as Logger } from 'aws-amplify/utils';
import { Trans, withTranslation } from 'react-i18next';

import { withRouter } from '../../hooks/withRouter';
import Context from '../../context';
import { createErrorMessage } from '../../context/globalStateHelper';
import { localeDateFormat } from '../../utils/i18n';
import { startOfGasDay } from '../../utils/gasday';

import './RelationshipForm.less';
import ModalWrapper, { modalConfirmClose } from '../ModalWrapper';
import { filterRelationshipPointAndDirection, isExternal, isShipper } from '../../utils/marketparty';
import {
  DIRECTION_NAMES,
  RELATIONSHIP_LOCATION_POINT_NAMES,
} from '../../constants/nominations';
import {
  MARKETPARTY_RELATIONSHIP_TYPES,
  MARKETPARTY_RELATIONSHIP_TYPES_NAMES,
} from '../../constants/relationships';
import { getNetworkLocationPoints } from '../../utils/networks';
import { STATUS_NAMES } from '../../constants/status';

const logger = new Logger('new-relation-ship:form');

const RelationshipForm = (props) => {
  const {
    t, relationship, handleClose, navigate,
  } = props;
  const context = useContext(Context);
  const {
    marketPartyIndex,
    getSelectedMarketParty,
    updateMarketpartyIndex,
    selectedMarketPartyId,
    handleDbChange,
    isReadOnlyAdmin,
    isAdmin,
  } = context;
  const [isSaving, setSaving] = useState(false);
  const [isButtonDisabled, setButtonDisabled] = useState(true);
  const [form] = Form.useForm();

  useEffect(() => {
    if (marketPartyIndex.length === 0) updateMarketpartyIndex();
  }, []);

  const closeModal = () => {
    navigate('/relationships-created');
  };

  const isUpdateRelationship = () => Boolean(relationship);

  const getButtonDisabled = () => {
    const {
      start,
      end,
      marketPartyId,
      type,
      params,
    } = form.getFieldsValue();
    const isFieldEmpty = [start, end, marketPartyId, type].includes(undefined);

    return !isUpdateRelationship()
      && (isFieldEmpty || (type === MARKETPARTY_RELATIONSHIP_TYPES.AUTHORIZE_TO_NOMINATE && params === undefined));
  };

  const handleCancel = () => (isButtonDisabled
    ? closeModal()
    : modalConfirmClose(t, closeModal));

  const handleCreate = async (body) => {
    if (selectedMarketPartyId !== undefined && !isAdmin() && !isReadOnlyAdmin()) {
      const {
        start, end, marketPartyId, type,
      } = body;

      const [point, direction] = body.params ? body.params.split('#') : [];
      const params = point && direction
        ? { point, direction }
        : undefined;

      try {
        setSaving(true);
        const relationshipUrl = `/marketparties/${selectedMarketPartyId}/relationships`;
        const { body: responseBody } = await API.post({
          apiName: 'FINTSO',
          path: relationshipUrl,
          options: {
            body: {
              marketPartyId,
              type,
              start: start.format('YYYY-MM-DD'),
              end: end.format('YYYY-MM-DD'),
              ...(params ? { params } : undefined),
            },
          },
        }).response;
        const newRelationship = await responseBody.json();

        handleDbChange('Relationships', 'create', newRelationship);

        notification.success({
          message: t('relationships.message.createdSuccessfully'),
          description: '',
        });
        form.resetFields();
        closeModal();
      } catch (err) {
        notification.error({
          className: 'notification-error',
          message: t('relationships.message.errorCreating'),
          // TODO: translatable error message
          description: await createErrorMessage(err),
        });
        logger.error(await createErrorMessage(err, true));
      } finally {
        setSaving(false);
      }
    }
  };

  const handleUpdate = async (body) => {
    if (selectedMarketPartyId !== undefined && isAdmin()) {
      const { start, end, status } = body;
      const { id: relationshipId, marketPartyIdA } = relationship;
      try {
        setSaving(true);
        const relationshipUrl = `/marketparties/${marketPartyIdA}/relationships/${relationshipId}`;
        const { body: responseBody } = await API.patch({
          apiName: 'FINTSO',
          path: relationshipUrl,
          options: {
            body: {
              start: start.format('YYYY-MM-DD'),
              end: end.format('YYYY-MM-DD'),
              status,
            },
          },
        }).response;
        const newRelationship = await responseBody.json();

        handleDbChange('Relationships', 'update', newRelationship);

        notification.success({
          message: t('relationships.message.updatedSuccessfully'),
          description: '',
        });
        form.resetFields();
        handleClose();
      } catch (err) {
        notification.error({
          className: 'notification-error',
          message: t('relationships.message.errorUpdating'),
          // TODO: translatable error message
          description: await createErrorMessage(err),
        });
        logger.error(await createErrorMessage(err, true));
      } finally {
        setSaving(false);
      }
    }
  };

  const handleSearch = (inputValue, option) => (option.key.includes(lowerCase(inputValue)));

  const earliestPossibleDay = (type) => {
    const now = moment();
    const {
      start,
      type: relationshipType,
    } = form.getFieldsValue();

    if (type === 'end' && start !== undefined && now.isBefore(startOfGasDay(start))) {
      return startOfGasDay(start);
    }

    if (!isAdmin() && relationshipType === MARKETPARTY_RELATIONSHIP_TYPES.AUTHORIZE_TO_NOMINATE) {
      const RELATIONSHIP_LEAD_TIME = 1; // number of days
      return startOfGasDay(now.add(RELATIONSHIP_LEAD_TIME, 'days'));
    }

    return startOfGasDay(now);
  };

  const isDateDisabled = (timestamp, type) => (
    startOfGasDay(timestamp).isBefore(earliestPossibleDay(type).format('YYYY-MM-DD'))
  );

  const renderShipperAuthorizationSelectlist = () => {
    const isUpdate = isUpdateRelationship();
    if (isUpdate && !relationship.params) {
      return null;
    }

    const selectedMarketParty = isUpdate
      ? marketPartyIndex.find(({ id }) => id === relationship.marketPartyIdA)
      : getSelectedMarketParty();
    const type = Form.useWatch('type', form);

    if (type === MARKETPARTY_RELATIONSHIP_TYPES.AUTHORIZE_TO_NOMINATE) {
      const marketPartyName = selectedMarketParty.name;

      return (
        <Form.Item
          label={<Trans i18nKey="relationships.label.authoriseShipperCheckbox" values={{ marketPartyName }} />}
          className="relationship-form__additional-permissions"
          name="params"
          initialValue={
            isUpdate
              ? `${relationship.params.point}#${relationship.params.direction}`
              : undefined
            }
          rules={[{ required: true }]}
        >
          <Select
            className="relationship-form__select-authorization"
            data-testid="relationship-form__select-authorization"
            disabled={isUpdateRelationship()}
          >
            {RELATIONSHIP_LOCATION_POINT_NAMES
              .flatMap(point => DIRECTION_NAMES
                .filter(direction => filterRelationshipPointAndDirection(point, direction))
                .map(direction => (
                  <Select.Option
                    value={`${point}#${direction}`}
                    key={`relationship-form__authorise-${point}_${direction}-select`}
                    className={`relationship-form__authorise-${point}_${direction}-select`}
                    data-testid={`relationship-form__authorise-${point}_${direction}-select`}
                  >
                    {t(`common.label.${point}`)}
                    {' '}
                    —
                    {' '}
                    {t(`common.label.${direction}`)}
                  </Select.Option>
                )))}
          </Select>
        </Form.Item>
      );
    }
    return null;
  };

  const renderStatusField = () => {
    const isUpdate = isUpdateRelationship();

    if (isUpdate && isAdmin()) {
      return (
        <Form.Item
          label={t('common.label.status')}
          className="relationship-form__status"
          name="status"
          initialValue={isUpdate ? relationship.status : undefined}
        >
          <Select
            className="relationship-form__select-status"
            data-testid="relationship-form__select-status"
          >
            {STATUS_NAMES.map((status) => (
              <Select.Option
                value={status}
                key={`relationship-form__status-select-${status}`}
                className={`relationship-form__status-select-${status}`}
                data-testid={`relationship-form__status-select-${status}`}
              >
                {t(`common.status.${status}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      );
    }
    return null;
  };

  const renderDate = (type) => (
    <Form.Item
      label={t(`relationships.label.${type}Date`)}
      colon={false}
      initialValue={isUpdateRelationship() ? moment(relationship[type], 'YYYY-MM-DD') : undefined}
      name={type}
      rules={[{ required: true, message: t('common.form.dateErrorMessage') }]}
    >
      <DatePicker
        className={`relationship-form__${type}-date`}
        data-testid={`relationship-form__${type}-date`}
        format={localeDateFormat()}
        disabledDate={date => isDateDisabled(date, type)}
      />
    </Form.Item>
  );

  // TODO: This filtering can be removed once shipperGroup feature is made available for all users.
  const filterSelectRelationship = (type) => {
    if (type === MARKETPARTY_RELATIONSHIP_TYPES.SHIPPER_GROUP) {
      return isAdmin(true);
    }
    return true;
  };

  const renderTypeSelector = () => (
    <Form.Item
      label={t('relationships.label.type')}
      name="type"
      initialValue={isUpdateRelationship() ? relationship.type : MARKETPARTY_RELATIONSHIP_TYPES.SHIPPER}
      rules={[{ required: true, message: t('common.form.emptyInputFieldErrorMessage') }]}
    >
      <Select
        className="relationship-form__select-type"
        data-testid="relationship-form__select-type"
        disabled={isUpdateRelationship()}
      >
        {MARKETPARTY_RELATIONSHIP_TYPES_NAMES.filter(filterSelectRelationship).map(name => (
          <Select.Option
            key={lowerCase(name)}
            value={name}
            onClick={(obj) => {
              if (obj.key === MARKETPARTY_RELATIONSHIP_TYPES.AUTHORIZE_TO_NOMINATE) {
                // reset Date when relationship type changes
                form.setFieldsValue({
                  start: undefined,
                  end: undefined,
                });
              }
            }}
            data-testid={`relationship-form__type-option-${name}`}
          >
            {t(`relationships.label.types.${name}`)}
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
  );

  const filterMarketPartyIndexes = (mpIndex, params, type) => {
    if (type === MARKETPARTY_RELATIONSHIP_TYPES.SHIPPER || type === MARKETPARTY_RELATIONSHIP_TYPES.SHIPPER_GROUP) {
      return isShipper(mpIndex);
    }

    // When type is authorizeOnBehalf, params should be selected first
    if (!params) {
      return false;
    }

    const [point] = params ? params.split('#') : [];

    const isInSelectedPoint = mpIndex.network.some((nw) => {
      const points = getNetworkLocationPoints(nw);
      return points.includes(point);
    });
    return (isInSelectedPoint && (isShipper(mpIndex) || isExternal(mpIndex)));
  };

  const checkIsShipperSelectionDisabled = () => {
    const { type, params } = form.getFieldsValue();
    return (
      type === MARKETPARTY_RELATIONSHIP_TYPES.AUTHORIZE_TO_NOMINATE
      && !params
    );
  };

  const renderShipperSelector = () => {
    const params = Form.useWatch('params', form);
    const type = Form.useWatch('type', form);

    return (
      <Form.Item
        label={
        form.getFieldValue('type') === MARKETPARTY_RELATIONSHIP_TYPES.AUTHORIZE_TO_NOMINATE
          ? t('relationships.label.marketparty')
          : t('relationships.label.shipper')
      }
        colon={false}
        name="marketPartyId"
        initialValue={isUpdateRelationship() ? relationship.marketPartyIdB : undefined}
        rules={[{ required: true, message: t('common.form.emptyInputFieldErrorMessage') }]}
      >
        <Select
          className="relationship-form__select-shipper"
          data-testid="relationship-form__select-shipper"
          filterOption={handleSearch}
          disabled={
          isUpdateRelationship() || checkIsShipperSelectionDisabled()
        }
          showSearch
          suffixIcon={<SearchOutlined />}
        >
          {marketPartyIndex
            .filter((index) => filterMarketPartyIndexes(index, params, type))
            .map((item) => (
              <Select.Option
                key={lowerCase(item.name)}
                value={item.id}
                data-testid="relationship-form__options"
              >
                {item.name}
              </Select.Option>
            ))}
        </Select>
      </Form.Item>
    );
  };

  const renderContent = () => (
    <Form
      className="relationship-form__form"
      data-testid="relationship-form__form"
      onFinish={isUpdateRelationship() ? handleUpdate : handleCreate}
      form={form}
      onValuesChange={() => setButtonDisabled(getButtonDisabled())}
      layout="vertical"
    >
      <Row>
        <Col span={16}>{renderTypeSelector()}</Col>
      </Row>
      <Row>
        <Col span={16}>{renderShipperAuthorizationSelectlist()}</Col>
      </Row>
      <Row>
        <Col span={16}>{renderShipperSelector()}</Col>
      </Row>
      <Row>
        <Col span={16}>{renderStatusField()}</Col>
      </Row>
      <Row>
        <Col span={8}>{renderDate('start')}</Col>
        <Col span={1}>
          <div className="relationship-form__form__input-divider">-</div>
        </Col>
        <Col span={8}>{renderDate('end')}</Col>
      </Row>
    </Form>
  );

  const renderActionButtons = () => [
    <Button
      className="relationship-form__cancel-button"
      data-testid="relationship-form__cancel-button"
      key="cancel"
      onClick={handleClose || handleCancel}
    >
      {t('common.button.cancel')}
    </Button>,
    <Button
      className="relationship-form__create-button"
      data-testid="relationship-form__create-button"
      key="save"
      loading={isSaving}
      type="primary"
      onClick={() => form.submit()}
      disabled={isButtonDisabled}
    >
      {isUpdateRelationship() ? t('common.button.update') : t('common.button.create')}
    </Button>,
  ];

  return (
    <ModalWrapper
      modalClassName="relationship-form"
      title={isUpdateRelationship() ? t('relationships.title.updateRelationship') : t('relationships.title.newRelationship')}
      handleClose={handleClose || handleCancel}
      actionButtons={renderActionButtons()}
    >
      {renderContent()}
    </ModalWrapper>
  );
};

RelationshipForm.propTypes = {
  t: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  handleClose: PropTypes.func,
  relationship: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    marketPartyIdA: PropTypes.string.isRequired,
    marketPartyIdB: PropTypes.string.isRequired,
    params: PropTypes.shape({
      point: PropTypes.string.isRequired,
      direction: PropTypes.string.isRequired,
    }),
    status: PropTypes.string,
  }),
};

RelationshipForm.displayName = 'RelationshipForm';
RelationshipForm.contextType = Context;

const TRANSLATED = withTranslation()(RelationshipForm);

export default withRouter(TRANSLATED);
export {
  RelationshipForm as PureComponent,
};
